import {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react'

import FormWithControls from 'app/shared-components/FormWithControls'
import requests from 'app/requests/Order/OrderBilling.requests'
import useFieldsWithClassName from 'app/hooks/useFieldsWithClassName'
import useEnumValues from 'app/hooks/useEnumValues'
import {
    Position,
} from 'app/Apps/OrderManagement/Orders/orders.types'
import {
    PositionType,
} from 'app/enums/label.enums'

import useStyles from './PositionForm.style'
import getFields from './positionForm.fields'
import useLoadAutoPopulatePosition from './hooks/useLoadAutoPopulatePosition'
import useGetPositionTypeMapping from './hooks/useGetPositionTypeMapping'

type Props = {
    value: Position,
    name: string,
    onSuccess: () => void,
    closeOpenedRow: () => void,
    invoiceId: number,
    disabled?: boolean,
    exists?: boolean,
}

const defaultProps = {
    disabled: false,
    exists: false,
}

const PositionForm = ({
    closeOpenedRow,
    invoiceId,
    onSuccess,
    value,
    disabled,
    exists,
    name,
}: Props) => {
    const {
        classes,
    } = useStyles()
    const getAllowedValues = useEnumValues()

    const [
        data,
        setData,
    ] = useState<Position>(value)

    const [
        selectedPositionCode,
        setPositionCode,
    ] = useState<string>(null)

    const [
        selectedPositionType,
        setPositionType,
    ] = useState<PositionType>(value?.positionType)

    const {
        positionTypeMapping,
    } = useGetPositionTypeMapping()

    const fields = useMemo(() => {
        const allowedCodesObject = positionTypeMapping[data.positionType]
            || positionTypeMapping.LEASE

        return getFields({
            positionCodeAllowedValues: getAllowedValues(allowedCodesObject),
            getAllowedValues,
        })
    }, [
        data.positionType,
        getAllowedValues,
        positionTypeMapping,
    ])

    const fieldsWithClassName = useFieldsWithClassName(fields, classes)

    const {
        data: autoPopulatePositionData,
        isDataReady,
    } = useLoadAutoPopulatePosition({
        invoiceId,
        positionCode: data.positionCode,
        enabled: !exists,
    })

    useEffect(() => {
        if (data.positionType !== selectedPositionType) {
            setData({
                ...data,
                positionCode: null,
            })
            setPositionType(data.positionType)
        }
    }, [
        data,
        selectedPositionType,
    ])

    useEffect(() => {
        if (selectedPositionCode !== data.positionCode && isDataReady) {
            setData({
                ...data,
                ...autoPopulatePositionData,
            })

            setPositionCode(data.positionCode)
        }
    }, [
        autoPopulatePositionData,
        data,
        isDataReady,
        selectedPositionCode,
    ])

    const onSuccessUpdate = useCallback((): void => {
        onSuccess()
        closeOpenedRow()
    }, [
        closeOpenedRow,
        onSuccess,
    ])

    const requestParams = useMemo(() => {
        return exists ? requests.updatePosition({
            outgoingInvoiceId: invoiceId, positionId: value?.id,
        }) : requests.createPosition({
            outgoingInvoiceId: invoiceId,
        })
    }, [
        invoiceId,
        exists,
        value.id,
    ])

    return (
        <FormWithControls
            value={data}
            name={name}
            className={classes.rowFormContainer}
            fields={fieldsWithClassName}
            setExternalValue={setData}
            onSuccess={onSuccessUpdate}
            onCancel={closeOpenedRow}
            disabled={disabled}
            requestParams={requestParams}
            exists={exists}
        />
    )
}

PositionForm.defaultProps = defaultProps

export default PositionForm
